import React from "react";
import { splitFormControlWrapperProps } from "@cmp-src/form/FormControlWrapper/helpers";
import { FormControlWrapper } from "@cmp-src/form/FormControlWrapper";
import {
  Category,
  CategorySelectProps,
  CategorySelectPropsWithFormControl
} from "./types";
import { Popover } from "@cmp-src/overlay/Popover/Popover";
import { ScrollArea } from "@cmp-src/primitives/scroll-area";
import { Button } from "@cmp-src/primitives/button";
import { TextInput } from "@cmp-src/form/TextInput";
import {
  addParentReferences,
  findCategoryPath,
  flattenCategories
} from "./helpers";
import Fuse from "fuse.js";
import { CategorySelectList } from "@cmp-src/form/CategorySelect/CategorySelectList";
import { SearchIcon } from "@cmp-src/icons/icons";
import { useDisclosure } from "@gettactic/helpers";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { MobileDrawer } from "@cmp-src/mobile/MobileDrawer/MobileDrawer";

export const CategorySelectInput = React.forwardRef<
  HTMLInputElement,
  CategorySelectProps
>((props, ref) => {
  const { categories, trigger, onSelect, value } = props;

  const { isBreakpoint } = useBreakpoint();

  const isMd = isBreakpoint("md");

  const popoverDisclosure = useDisclosure();
  const [searchTerm, setSearchTerm] = React.useState("");
  const [internalSelectedCategory, setInternalSelectedCategory] =
    React.useState<Category | null>(null);
  const [navigationStack, setNavigationStack] = React.useState<Category[]>([]);

  // Determine if the component is controlled
  const isControlled = value !== undefined && onSelect !== undefined;
  const selectedCategory = isControlled ? value : internalSelectedCategory;

  // Memoize the categoriesWithParents to prevent unnecessary recalculations
  const categoriesWithParents = React.useMemo(() => {
    return addParentReferences(categories);
  }, [categories]);

  // Memoize the Fuse instance and flattened categories
  const { fuse } = React.useMemo(() => {
    const allCats = flattenCategories(categoriesWithParents);
    const fuseInstance = new Fuse(allCats, {
      keys: ["name", "keywords"],
      threshold: 0.3,
      distance: 100
    });
    return { fuse: fuseInstance, allCategories: allCats };
  }, [categoriesWithParents]);

  // Memoize the search results
  const currentCategories = React.useMemo(() => {
    if (searchTerm) {
      const results = fuse.search(searchTerm);

      return results.map((result) => result.item);
    }
    const currentParent =
      navigationStack[navigationStack.length - 1] || categoriesWithParents;
    return currentParent.children || [];
  }, [navigationStack, searchTerm, fuse, categoriesWithParents]);

  React.useEffect(() => {
    if (
      popoverDisclosure.isOpen &&
      selectedCategory &&
      navigationStack.length === 0
    ) {
      setNavigationStack(findCategoryPath(selectedCategory.id, categories));
    }
  }, [popoverDisclosure.isOpen, selectedCategory, categories]);

  const handleSelect = (category: Category) => {
    if (category.children) {
      if (searchTerm) {
        const path = findCategoryPath(category.id, categoriesWithParents);
        setNavigationStack([...path, category]);
        setSearchTerm("");
      } else {
        setNavigationStack([...navigationStack, category]);
      }
    } else {
      if (isControlled) {
        onSelect(category);
      } else {
        setInternalSelectedCategory(category);
      }
      popoverDisclosure.close();
    }
  };

  const handleBack = () => {
    setNavigationStack(navigationStack.slice(0, -1));
  };

  const handleOpenChange = (open: boolean) => {
    popoverDisclosure.onOpenChange(open);
    if (!open) {
      setTimeout(() => {
        setNavigationStack([]);
        setSearchTerm("");
      }, 200);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const triggerContent = (
    <>
      {trigger || (
        <Button
          variant="trigger"
          className={"w-full px-2 shadow-lg md:shadow-none"}
          size={"trigger"}
        >
          <span className={"block max-w-full overflow-hidden truncate"}>
            {selectedCategory?.name || "Select Category"}
          </span>
        </Button>
      )}
    </>
  );

  const content = (
    <>
      <div className={"w-full"}>
        <div className="relative border-b-1 border-b-neutral-100 p-0.5">
          <TextInput
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={"w-full"}
            wrapperClassName={"border-0 shadow-none px-4"}
            inputClassName={"h-5 py-3 outline-none"}
            leftIcon={<SearchIcon className={"size-4"} />}
          />
          {searchTerm && (
            <button
              onClick={handleClearSearch}
              className="absolute top-2.5 right-2 text-muted-foreground hover:text-foreground"
            />
          )}
        </div>
        <ScrollArea className="h-[250px] w-full">
          <CategorySelectList
            categories={currentCategories}
            onSelect={handleSelect}
            onBack={navigationStack.length > 0 ? handleBack : undefined}
            navigationStack={navigationStack}
            showPath={!!searchTerm}
            allCategories={categoriesWithParents}
          />
        </ScrollArea>
      </div>
    </>
  );

  if (!isMd) {
    return (
      <>
        <button
          onClick={() => popoverDisclosure.toggle()}
          className={"group inline-flex max-w-full min-w-0 gap-2 text-left"}
        >
          {triggerContent}
        </button>
        <MobileDrawer
          isOpen={popoverDisclosure.isOpen}
          onOpenChange={handleOpenChange}
        >
          {content}
        </MobileDrawer>
      </>
    );
  }

  return (
    <Popover
      className={"w-[252px]"}
      bodyProps={{ className: "p-0 w-full" }}
      content={content}
      open={popoverDisclosure.isOpen}
      onOpenChange={handleOpenChange}
      triggerAsChild={false}
    >
      {triggerContent}
    </Popover>
  );
});

CategorySelectInput.displayName = "CategorySelectInput";

export const CategorySelect = React.forwardRef<
  HTMLInputElement,
  CategorySelectPropsWithFormControl
>((props, ref) => {
  const [wrapperProps, inputProps] = splitFormControlWrapperProps(props);

  return (
    <>
      <FormControlWrapper variant={"unstyled"} {...wrapperProps}>
        <CategorySelectInput ref={ref} {...inputProps} />
      </FormControlWrapper>
    </>
  );
});

CategorySelect.displayName = "CategorySelect";
